import React, {SetStateAction, useState, Dispatch} from 'react';
import {Button as FpButton} from '@finpay-development/shared-components';

import * as Yup from 'yup';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import LinearProgress from '@mui/material/LinearProgress';
import {useFormik} from 'formik';
import {
  Typography,
} from '@mui/material';
import {useDispatch, useSelector} from 'react-redux';
import {AppDispatch} from '../../../shared/state/store';
import {RootState} from '../../../shared/state/root-reducer';
import {ClientSelect} from '../../../shared/components/client-select';
import {
  specialistClientService
} from '@finpay-development/shared-platform/lib/esm/implementation-specialist/services/implementation-client-service';
import {showErrorStatus, showStatus} from '@finpay-development/shared-platform/lib/esm/security/state/user-slice';
import {ValidationError} from 'yup';
import {configSettings} from '@finpay-development/shared-platform/lib/esm/shared/configuration/config-settings';
import {patientService} from '@finpay-development/shared-platform/lib/esm/patient/services/patient-service';

interface UploadProps {
  bucketName: string;
  setIsModalOpen: Dispatch<SetStateAction<boolean>>;
  isOpen: boolean;
}

type UploadFormValues = {
  client: {
    clientId: string;
    clientName: string;
  }[]
  file:  File | null
}

export const UploadModal = ({
                              bucketName,
                              setIsModalOpen,
                              isOpen,
                            }: UploadProps) => {

  const clients = useSelector(
      (state: RootState) =>
          state.implementationContext.implementationSpecialistClient.filteredByIsActive,
  );

  const [isLoading, setIsLoading] = useState(false);

  const dispatch = useDispatch<AppDispatch>();

  const initialValues: UploadFormValues = {
    client: [],
    file: null,
  }

  const formik = useFormik({
    initialValues,
    onSubmit: async (values) => {
      const errorMsg = "Error while making PFR adjustments. If the problem persists, please reach out to a FinPay Administrator."
      try{

        setIsLoading(true)

        await Yup.object({
          client: Yup.array(Yup.object()).min(1, 'Please select a client').max(1, 'You can only select 1 client'),
          file: Yup.mixed<File>()
          .required('Please select a file to upload')
          .test('ext', 'File must be a csv file', (v) => {
            if(!v) return true
            if (!(v instanceof File)) return false;
            return /\.csv$/i.test(v.name) && [
              'text/csv',
              // these show up on some Windows/Safari uploads
              'application/vnd.ms-excel',
              'application/csv',
              'text/plain',
            ].includes(v.type)
          }),
        }).validate(values, {abortEarly: false})
        
        //following FinAdvisor Rate Upload file naming pattern
        const now = new Date();
        const formattedTime = now.toISOString().replace(/[:.]/g, "-");

        const key = `${values.client?.[0]?.clientId}/uploadFile/${values.client?.[0]?.clientId}_${formattedTime}.csv`

        const response = await specialistClientService.uploadRateFileToS3(
            key,
            bucketName,
            values.file,
        );

        if (response.hasErrors) {
          dispatch(showErrorStatus(errorMsg));
          return
        }

        const pfrResponse = await patientService.createPfrAdjustments({
          bucket: bucketName,
          key
        })

        if (pfrResponse.hasErrors) {
          dispatch(showErrorStatus(errorMsg));
          return
        }

        dispatch(showStatus("PFR Adjustments have been initiated"))
        formik.resetForm()
        setIsModalOpen(false)

      } catch(e) {

        if(e instanceof ValidationError){
          const errors = {};
          let message = `Please resolve the following issues: `;
          e.inner.forEach((error: any) => {
            // @ts-ignore
            errors[error.path] = error.message;
            message += ` ${error.message};`;
          });
          dispatch(showErrorStatus(message))
        }else{
          dispatch(showErrorStatus(errorMsg))
        }

      } finally {

        setIsLoading(false)

      }
    }
  });

  return (
      <div>
        <Dialog
            scroll="body"
            className="modal client-modal"
            open={isOpen}
            fullWidth={true}
            maxWidth="md"
        >
          <DialogTitle id="form-dialog-title">
            <Grid item xs={12} px={1}>
              <Typography variant="h6">Attached Documents</Typography>
            </Grid>
          </DialogTitle>

          <DialogContent>
            <form>
              <Grid container spacing={2} className="px-2 pb-2">
                <Grid item xs={12} className="MuiTextField-root">
                  <ClientSelect
                      options={clients}
                      onChange={(event, newValue) => {
                        formik.setFieldValue('client', newValue);
                      }}
                      value={formik.values?.client}
                      maxSelections={1}
                  />
                </Grid>
                <Grid item xs={12}>
                  <label htmlFor="upload-fileS3">
                    <input
                        style={{display: 'none'}}
                        id="upload-fileS3"
                        name="file"
                        type="file"
                        onChange={(e: any) => {
                          formik.setFieldValue('file', e.currentTarget.files[0]);
                        }}
                    />

                    <Button
                        className="doc-modal"
                        color="primary"
                        variant="contained"
                        component="span"
                    >
                      Select File
                    </Button>
                  </label>

                </Grid>

                <Grid item xs={12}>
                  {formik.values.file?.name}
                </Grid>

                {isLoading && (
                    <Grid item xs={12}>
                      <LinearProgress/>
                    </Grid>
                )}
              </Grid>
            </form>
          </DialogContent>

          <DialogActions>
            <Grid container wrap="nowrap" px={1}>
              <Grid item sx={{width: '100%'}}>
                <Button
                    className="doc-modal"
                    component="a"
                    href={configSettings.pfr_adjustments_template_url}
                    download={!configSettings.pfr_adjustments_template_url}
                    color="primary"
                    variant="outlined"
                >
                  Download CSV Template
                </Button>
              </Grid>
              <Grid container item spacing={2} justifyContent="flex-end" alignItems="center">
                <Grid item>
                  <Button
                      className="doc-modal"
                      onClick={() => {
                        formik.resetForm();
                        setIsModalOpen(false);
                      }}
                      color="primary"
                      variant="contained"
                  >
                    Close
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                      className="doc-modal"
                      onClick={formik.submitForm}
                  >
                    Upload File
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </DialogActions>
        </Dialog>
      </div>
  );
};


export const UploadModalTrigger = () => {

  const [isModalOpen, setIsModalOpen] = useState(false);

  const bucketName = configSettings.aws_pfr_adjustments_s3_bucket_name

  return (
    <>
      <FpButton
        type="primary"
        onClick={() => setIsModalOpen(true)}
        marginTop={5}
      >
        Upload PFR Adjustments
      </FpButton>
      <UploadModal
        bucketName={bucketName}
        setIsModalOpen={setIsModalOpen}
        isOpen={isModalOpen}
      />
    </>
  );
};

interface IOCUploadProps {
  bucketName: string;
  setIsModalOpen: Dispatch<SetStateAction<boolean>>;
  isOpen: boolean;
  templateUrl: string;
}

export const IOCUploadModal = ({
  bucketName,
  setIsModalOpen,
  isOpen,
  templateUrl,
}: IOCUploadProps) => {

  const clients = useSelector(
      (state: RootState) =>
          state.implementationContext.implementationSpecialistClient.filteredByIsActive,
  );

  const [isLoading, setIsLoading] = useState(false);

  const dispatch = useDispatch<AppDispatch>();

  const initialValues: UploadFormValues = {
    client: [],
    file: null,
  }

  const formik = useFormik({
    initialValues,
    onSubmit: async (values) => {
      const errorMsg = "Error while making IOC imports. If the problem persists, please reach out to a FinPay Administrator."
      try{

        setIsLoading(true)

        await Yup.object({
          client: Yup.array(Yup.object()).min(1, 'Please select a client').max(1, 'You can only select 1 client'),
          file: Yup.mixed<File>()
          .required('Please select a file to upload')
          .test('ext', 'File must be a csv file', (v) => {
            if(!v) return true
            if (!(v instanceof File)) return false;
            return /\.csv$/i.test(v.name) && [
              'text/csv',
              // these show up on some Windows/Safari uploads
              'application/vnd.ms-excel',
              'application/csv',
              'text/plain',
            ].includes(v.type)
          }),
        }).validate(values, {abortEarly: false})
        
        //following FinAdvisor Rate Upload file naming pattern
        const now = new Date();
        const formattedTime = now.toISOString().replace(/[:.]/g, "-");

        const key = `${values.client?.[0]?.clientId}/uploadFile/${values.client?.[0]?.clientId}_${formattedTime}.csv`

        const response = await specialistClientService.uploadRateFileToS3(
            key,
            bucketName,
            values.file,
        );

        if (response.hasErrors) {
          dispatch(showErrorStatus(errorMsg));
          return
        }

        const iocResponse = await patientService.createBulkIocImport({
          bucket: bucketName,
          key
        })

        if (iocResponse.hasErrors) {
          dispatch(showErrorStatus(errorMsg));
          return
        }

        dispatch(showStatus("IOC Imports have been initiated"))
        formik.resetForm()
        setIsModalOpen(false)

      } catch(e) {

        if(e instanceof ValidationError){
          const errors = {};
          let message = `Please resolve the following issues: `;
          e.inner.forEach((error: any) => {
            // @ts-ignore
            errors[error.path] = error.message;
            message += ` ${error.message};`;
          });
          dispatch(showErrorStatus(message))
        }else{
          dispatch(showErrorStatus(errorMsg))
        }

      } finally {

        setIsLoading(false)

      }
    }
  });

  return (
      <div>
        <Dialog
            scroll="body"
            className="modal client-modal"
            open={isOpen}
            fullWidth={true}
            maxWidth="md"
        >
          <DialogTitle id="form-dialog-title">
            <Grid item xs={12} px={1}>
              <Typography variant="h6">Attached Documents</Typography>
            </Grid>
          </DialogTitle>

          <DialogContent>
            <form>
              <Grid container spacing={2} className="px-2 pb-2">
                <Grid item xs={12} className="MuiTextField-root">
                  <ClientSelect
                      options={clients}
                      onChange={(event, newValue) => {
                        formik.setFieldValue('client', newValue);
                      }}
                      value={formik.values?.client}
                      maxSelections={1}
                  />
                </Grid>
                <Grid item xs={12}>
                  <label htmlFor="upload-ioc-fileS3">
                    <input
                        style={{display: 'none'}}
                        id="upload-ioc-fileS3"
                        name="file"
                        type="file"
                        onChange={(e: any) => {
                          formik.setFieldValue('file', e.currentTarget.files[0]);
                        }}
                    />

                    <Button
                        className="doc-modal"
                        color="primary"
                        variant="contained"
                        component="span"
                    >
                      Select File
                    </Button>
                  </label>

                </Grid>

                <Grid item xs={12}>
                  {formik.values.file?.name}
                </Grid>

                {isLoading && (
                    <Grid item xs={12}>
                      <LinearProgress/>
                    </Grid>
                )}
              </Grid>
            </form>
          </DialogContent>

          <DialogActions>
            <Grid container wrap="nowrap" px={1}>
              <Grid item sx={{width: '100%'}}>
                <Button
                    className="doc-modal"
                    component="a"
                    href={templateUrl}
                    download={!templateUrl}
                    color="primary"
                    variant="outlined"
                >
                  Download CSV Template
                </Button>
              </Grid>
              <Grid container item spacing={2} justifyContent="flex-end" alignItems="center">
                <Grid item>
                  <Button
                      className="doc-modal"
                      onClick={() => {
                        formik.resetForm();
                        setIsModalOpen(false);
                      }}
                      color="primary"
                      variant="contained"
                  >
                    Close
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                      className="doc-modal"
                      onClick={formik.submitForm}
                  >
                    Upload File
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </DialogActions>
        </Dialog>
      </div>
  );
};

export const UploadBulkIOCModalTrigger = () => {

  const [isModalOpen, setIsModalOpen] = useState(false);

  const bucketName = configSettings.aws_pfr_adjustments_s3_bucket_name

  return (
    <>
      <FpButton
        type="primary"
        onClick={() => setIsModalOpen(true)}
        marginTop={5}
      >
        Upload Bulk IOCs
      </FpButton>
      <IOCUploadModal
        bucketName={bucketName}
        setIsModalOpen={setIsModalOpen}
        isOpen={isModalOpen}
        templateUrl={configSettings.bulk_ioc_import_template_url}
      />
    </>
  );
};
